import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { SignInWithAppleResponse } from '@capacitor-community/apple-sign-in';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CompleteRegistrationBody } from '@reach/interfaces/auth/auth.requests';
import { PlanSubscriptionInterface } from '@reach/interfaces/plans/plan.interface';
import { Nullable } from '@reach/interfaces/typescript/nullable';
import { Language } from '@reach/interfaces/user/language.enum';
import { UserFeatureFlags } from '@reach/interfaces/user/user-feature-flags';
import { UserType } from '@reach/interfaces/user/user-type.enum';
import { resolveSafeRedirectUrl } from '@reach/utils/string/resolve-safe-redirect-url';

import { AuthApiService, PlansApiService } from '~app-client/api/services';
import { CLIENT_ENVIRONMENT } from '~app-client/core/tokens';
import { ClientUser } from '~app-client/core/types';
import { ReachStorage } from '~app-client/core/utils';
import { userClientToSessionClientTransformer } from '~app-client-item/core/utils/session-transformer';
import { CommonSessionService } from '~ng-common-lib/session/session.service';

import { FirebaseDeviceService } from '../firebase-device.service';
import { AdTrackEvent, AdTrackingService } from '../pixel/ad-tracking.service';

/**
 * Service that handles all the logic regarding Session.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
	private static readonly LOGOUT_FIRST_ROUTES = ['/oauth'] as const;

	private readonly firebaseDeviceService = inject(FirebaseDeviceService);

	private user$$ = new BehaviorSubject<
		Nullable<ClientUser & { currentPlanSubscription: Nullable<PlanSubscriptionInterface> }>
	>(null);
	public user$ = this.user$$.asObservable();

	private loggedOut$$ = new Subject<void>();
	public loggedOut$ = this.loggedOut$$.asObservable();

	private loading$$ = new BehaviorSubject(false);
	public loading$ = this.loading$$.asObservable();

	private currentUser?: Partial<
		ClientUser & { currentPlanSubscription: Nullable<PlanSubscriptionInterface> }
	>;

	public async untilLoadingFinished(): Promise<void> {
		return await new Promise<void>((resolve) => {
			const stop$ = new Subject<void>();
			this.loading$.pipe(takeUntil(stop$)).subscribe((loading) => {
				if (!loading) {
					stop$.next();
					resolve();
				}
			});
		});
	}

	private _triedToLogin = false;
	public get triedToLogin(): boolean {
		return this._triedToLogin;
	}

	private lastUserId: string | null = null;

	private readonly authApi = inject(AuthApiService);
	private readonly plansApi = inject(PlansApiService);
	private readonly adTrackingService = inject(AdTrackingService);
	private readonly commonSessionService = inject(CommonSessionService);
	private readonly environment = inject(CLIENT_ENVIRONMENT);

	constructor() {
		this.user$.pipe(takeUntilDestroyed()).subscribe(async (user) => {
			await this.handleUserChange(user);
		});
	}

	public hasFeatureFlag(flag: UserFeatureFlags): boolean {
		return (this.user?.featureFlags || []).includes(flag);
	}

	/**
	 * Routes that must log out before any session ping.
	 * Used during app bootstrap so an existing session cannot redirect away
	 * before logout completes (e.g. OAuth "use another account").
	 */
	public shouldSkipSessionPing(): boolean {
		return this.isLogoutFirstRoute();
	}

	public addData(data: Partial<ClientUser>): void {
		this.currentUser = {
			...(this.currentUser || {}),
			...data,
		};

		this.emitChanges();
	}

	public get user(): ClientUser | null {
		return this.currentUser as ClientUser | null;
	}

	public async ping(): Promise<void> {
		try {
			this.loading$$.next(true);
			this._triedToLogin = true;

			const user = await this.authApi.ping();
			const currentPlan = await this.plansApi.getCurrentPlan();
			this.currentUser = {
				...(this.currentUser || {}),
				...user,
				currentPlanSubscription: currentPlan,
			};

			this.emitChanges();
			await this.checkSpecialActions();
		} catch (e) {
			console.error(e);
			if ((this.currentUser || null) !== null) {
				await this.logout();
			}
			this.handleRequestError(e);
		} finally {
			this.loading$$.next(false);
		}
	}

	public async login(usernameOrEmail: string, password: string): Promise<void> {
		try {
			this.loading$$.next(true);
			this._triedToLogin = true;
			const user = await this.authApi.login(usernameOrEmail.trim(), password);
			const currentPlan = await this.plansApi.getCurrentPlan();
			this.currentUser = {
				...(this.currentUser || {}),
				...user,
				currentPlanSubscription: currentPlan,
			};
			this.emitChanges();
			await this.checkSpecialActions();
		} catch (e) {
			this.currentUser = undefined;
			this.emitChanges();
			this.handleRequestError(e);
			throw e;
		} finally {
			this.loading$$.next(false);
		}
	}

	public async register(
		username: string,
		email: string,
		password: string,
		language: Language
	): Promise<void> {
		const user = await this.authApi.register({
			user: {
				username,
				email,
				password,
				language,
			},
		});
		this.currentUser = {
			...(this.currentUser || {}),
			...user,
		};
		this.emitChanges();
		await this.checkSpecialActions();
	}

	public async completeRegistration(data: CompleteRegistrationBody): Promise<void> {
		const user = await this.authApi.completeRegistration(data);
		this.currentUser = user;
		this.adTrackingService.track(AdTrackEvent.COMPLETE_REGISTRATION, {});
		this.emitChanges();
	}

	public async googleSignIn(
		authToken: string,
		language: Language,
		referralId?: string
	): Promise<void> {
		try {
			await this.socialSignInHandler(async () => {
				return await this.authApi.signInWithGoogle(authToken, language, referralId);
			});
		} catch (error) {}
	}

	public async appleSignIn(
		appleResponse: SignInWithAppleResponse,
		language: Language,
		referralId?: string
	): Promise<void> {
		try {
			await this.socialSignInHandler(async () => {
				return await this.authApi.signInWithApple(appleResponse, language, referralId);
			});
		} catch (error) {}
	}

	public async microsoftSignIn(
		accessToken: string,
		language: Language,
		referralId?: string
	): Promise<void> {
		try {
			await this.socialSignInHandler(async () => {
				return await this.authApi.signInWithMicrosoft(accessToken, language, referralId);
			});
		} catch (error) {
			console.error(error);
		}
	}

	/**
	 * TODO: To logout even with the api down, we can set a value in the storage
	 * saying that we should not ping.
	 */
	public async logout(): Promise<void> {
		try {
			await this.firebaseDeviceService.removeDevice().catch((e) => console.error(e));
			await this.authApi.logout();
			this._triedToLogin = false;
		} catch (e) {
			console.error(e);
		}
		this.currentUser = undefined;
		this.emitChanges();
		this.loggedOut$$.next();
		ReachStorage.clear();
	}

	private handleRequestError(e: unknown): void {
		if (e instanceof HttpErrorResponse) {
			return this.handleRequestHttpError(e);
		}

		return this.handleUnknownError(e);
	}

	private handleRequestHttpError(e: HttpErrorResponse): void {
		const status = e.status;
		switch (status) {
			case 403:
				return this.handleUnauthorizedError(e);
		}
		throw e;
	}

	/**
	 * TODO: Do we really need this?
	 */
	private handleUnauthorizedError(e: HttpErrorResponse): void {
		console.warn('Check if this is needed');
		throw e;
	}

	private handleUnknownError(e: unknown): void {
		console.error(e);
		throw e;
	}

	private async handleUserChange(user: ClientUser | null): Promise<void> {
		const userId = user?.id || null;

		if (this.lastUserId !== userId) {
			try {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const session = userClientToSessionClientTransformer(user!);
				this.commonSessionService.setSession(session);
				if (user) {
					this.firebaseDeviceService.addDevice();
				}
			} catch (error) {
				console.error(error);
			}
		}

		this.lastUserId = userId;

		if (this.canFollowRedirectUrl() && this.hasRedirectUrlInLocation()) {
			await this.checkSpecialActions();
		}
	}

	private emitChanges(): void {
		this.user$$.next(
			this.currentUser as ClientUser & {
				currentPlanSubscription: Nullable<PlanSubscriptionInterface>;
			}
		);
	}

	private async socialSignInHandler(socialLogin: () => Promise<unknown>): Promise<void> {
		try {
			await socialLogin();

			try {
				await this.ping();
				await this.checkSpecialActions();
			} catch (_) {}
		} catch (error) {
			this.currentUser = undefined;
			this.emitChanges();
			this.handleRequestError(error);
		}
	}

	private isLogoutFirstRoute(): boolean {
		try {
			const pathname = new URL(location as unknown as string).pathname;
			return SessionService.LOGOUT_FIRST_ROUTES.some(
				(route) => pathname === route || pathname.endsWith(route)
			);
		} catch {
			return false;
		}
	}

	private hasRedirectUrlInLocation(): boolean {
		try {
			const url = new URL(location as unknown as string);
			return (url.searchParams.get('redirectUrl') || '').trim() !== '';
		} catch {
			return false;
		}
	}

	private canFollowRedirectUrl(): boolean {
		if (this.isLogoutFirstRoute()) {
			return false;
		}

		const user = this.currentUser;

		return !!user?.id && !!user.emailVerified && user.type === UserType.ALPHA;
	}

	private redirectIfNeeded(): void {
		if (!this.canFollowRedirectUrl()) {
			return;
		}

		try {
			const url = new URL(location as unknown as string);
			const rawRedirectUrl = (url.searchParams.get('redirectUrl') || '').trim();
			if (rawRedirectUrl === '') {
				return;
			}

			const isNative = Capacitor.isNativePlatform();
			// Only follow http(s) redirects to our own frontend or API, never javascript: or
			// third-party URLs.
			const redirectUrl = resolveSafeRedirectUrl(
				decodeURIComponent(rawRedirectUrl),
				isNative ? this.environment.frontendUrl : url.origin,
				[this.environment.frontendUrl, this.environment.apiBaseUrl]
			);
			if (redirectUrl) {
				if (isNative) {
					Browser.open({ url: redirectUrl });
				} else {
					location = redirectUrl as unknown as Location;
				}
			}
		} catch (error) {}
	}

	private async checkSpecialActions(): Promise<void> {
		try {
			this.redirectIfNeeded();

			const url = new URL(location as unknown as string);
			const specialAction = decodeURIComponent(
				(url.searchParams.get('specialAction') || '').trim()
			);

			switch (specialAction) {
				default:
					break;
			}
		} catch (error) {}
	}
}
